/**
 * Complete Administration / Onboarding / Deployment / Billing enum reference.
 * Source: VelorHR Administration Platform FE Handoff (Rev-2, Aug-14-2026), section 18.
 *
 * RULE: send/receive the NUMERIC value in API requests, responses, and filters;
 * map to the readable label only for display. Role is the one exception - it is a
 * fixed STRING value, not a numeric enum.
 */

/* ---------------------------------------------------------------- Roles */
export const ROLES = {
  PLATFORM_ADMIN: 'PlatformAdmin',
  CIO: 'CIO',
  CFO: 'CFO',
  SUPPORT: 'Support',
};

// Workspace / navigation-group labels (the module a role owns).
export const ROLE_LABELS = {
  [ROLES.PLATFORM_ADMIN]: 'Platform Admin',
  [ROLES.CIO]: 'CIO Operations',
  [ROLES.CFO]: 'CFO Finance',
  [ROLES.SUPPORT]: 'Support',
};

// Role identity labels (the account's role name) - used for the user Role column
// and the header role chip, distinct from the workspace names above.
export const ROLE_NAME_LABELS = {
  [ROLES.PLATFORM_ADMIN]: 'Platform Admin',
  [ROLES.CIO]: 'CIO',
  [ROLES.CFO]: 'CFO',
  [ROLES.SUPPORT]: 'Support',
};

/* --------------------------------------------- OnboardingInvitationStatus */
export const OnboardingInvitationStatus = {
  Sent: 0,
  Submitted: 1,
  Expired: 2,
  Cancelled: 3,
};
export const OnboardingInvitationStatusLabels = {
  0: 'Sent',
  1: 'Submitted',
  2: 'Expired',
  3: 'Cancelled',
};
export const OnboardingInvitationStatusColors = {
  0: 'gold', // Sent - awaiting the customer
  1: 'green', // Submitted
  2: 'default', // Expired
  3: 'red', // Cancelled
};

/* ------------------------------------------------------------ ClientStatus */
export const ClientStatus = {
  Onboarding: 0,
  Active: 1,
  Suspended: 2,
  Prospect: 3,
  InformationRequested: 4,
  InformationSubmitted: 5,
  CommercialReview: 6,
  PaymentPending: 7,
  Provisioning: 8,
  Ready: 9,
};
export const ClientStatusLabels = {
  0: 'Onboarding',
  1: 'Active',
  2: 'Suspended',
  3: 'Prospect',
  4: 'Information Requested',
  5: 'Information Submitted',
  6: 'Commercial Review',
  7: 'Payment Pending',
  8: 'Provisioning',
  9: 'Ready',
};
export const ClientStatusColors = {
  0: 'default', // Onboarding
  1: 'green', // Active
  2: 'red', // Suspended
  3: 'default', // Prospect
  4: 'gold', // InformationRequested
  5: 'green', // InformationSubmitted
  6: 'gold', // CommercialReview
  7: 'gold', // PaymentPending
  8: 'orange', // Provisioning
  9: 'green', // Ready
};

/* -------------------------------------------------------- ClientContactType */
export const ClientContactType = {
  OrganizationAdmin: 0,
  ContractAdmin: 1,
  BillingContact: 2,
};
export const ClientContactTypeLabels = {
  0: 'Organization Admin',
  1: 'Contract Admin',
  2: 'Billing Contact',
};

/* --------------------------------------------------------------- BillingCycle */
export const BillingCycle = {
  Monthly: 0,
  Yearly: 1,
};
export const BillingCycleLabels = {
  0: 'Monthly',
  1: 'Yearly',
};

/* -------------------------------------------------------- BillingHandoffStatus */
export const BillingHandoffStatus = {
  ReadyForInvoice: 0,
  InvoiceCreated: 1,
  InvoiceSent: 2,
  Paid: 3,
};
export const BillingHandoffStatusLabels = {
  0: 'Ready For Invoice',
  1: 'Invoice Created',
  2: 'Invoice Sent',
  3: 'Paid',
};
export const BillingHandoffStatusColors = {
  0: 'gold', // ReadyForInvoice
  1: 'gold', // InvoiceCreated
  2: 'gold', // InvoiceSent
  3: 'green', // Paid
};

/* ------------------------------------------------------------ DeploymentStatus */
export const DeploymentStatus = {
  Pending: 0,
  Provisioning: 1,
  Ready: 2,
  Failed: 3,
};
export const DeploymentStatusLabels = {
  0: 'Pending',
  1: 'Provisioning',
  2: 'Ready',
  3: 'Failed',
};
export const DeploymentStatusColors = {
  0: 'default', // Pending
  1: 'orange', // Provisioning
  2: 'green', // Ready
  3: 'red', // Failed
};

/* --------------------------------------------------------------- InvoiceStatus */
export const InvoiceStatus = {
  Draft: 0,
  Sent: 1,
  Paid: 2,
  Overdue: 3,
  Voided: 4,
};
export const InvoiceStatusLabels = {
  0: 'Draft',
  1: 'Sent',
  2: 'Paid',
  3: 'Overdue',
  4: 'Voided',
};
export const InvoiceStatusColors = {
  0: 'default', // Draft
  1: 'gold', // Sent
  2: 'green', // Paid
  3: 'red', // Overdue
  4: 'volcano', // Voided
};

/* -------------------------------------------------------- InvoicePaymentMethod */
export const InvoicePaymentMethod = {
  Stripe: 0,
  BankTransfer: 1,
  Direct: 2,
  Other: 3,
};
export const InvoicePaymentMethodLabels = {
  0: 'Stripe',
  1: 'Bank Transfer',
  2: 'Direct',
  3: 'Other',
};

/* ------------------------------------ ClientAdministratorProvisioningStatus */
export const ClientAdminProvisioningStatus = {
  Pending: 0,
  Invited: 1,
  Activated: 2,
  Failed: 3,
};
export const ClientAdminProvisioningStatusLabels = {
  0: 'Pending',
  1: 'Invited',
  2: 'Activated',
  3: 'Failed',
};
export const ClientAdminProvisioningStatusColors = {
  0: 'default', // Pending
  1: 'gold', // Invited
  2: 'green', // Activated
  3: 'red', // Failed
};

/* --------------------------------------------- TicketStatus (Tech Support) */
/* Source: Sai's Admin CIO/CFO spec (section 4). Backend contract pending from
 * Mudassir's next handoff - values here are the working assumption. */
export const TicketStatus = {
  Open: 0,
  InProgress: 1,
  Resolved: 2,
};
export const TicketStatusLabels = {
  0: 'Open',
  1: 'In Progress',
  2: 'Resolved',
};
export const TicketStatusColors = {
  0: 'red', // Open
  1: 'gold', // In Progress
  2: 'green', // Resolved
};

/* ---------------------------------------------------------------- Helpers */

/** Build antd Select options [{ value:Number, label:String }] from a labels map. */
export const optionsFromLabels = (labels) =>
  Object.entries(labels).map(([value, label]) => ({ value: Number(value), label }));

/** Safe label lookup with a numeric-or-string key. */
export const labelOf = (labels, value) =>
  value === null || value === undefined ? '-' : labels[value] ?? String(value);
